/**
 * HVM Cart — UI modules. Every element initializes itself in
 * connectedCallback, so they survive any innerHTML swap of the drawer
 * (our region patches and third-party full swaps alike).
 */
(() => {
  const HVMCart = window.HVMCart;
  if (!HVMCart || customElements.get('hvm-line-stepper')) return;

  /* ------------------------------------------------------------ *
   * <hvm-line-stepper> — optimistic quantity stepper, one per line.
   * Emits `hvm:line:set` (bubbles); the drawer debounces per line.
   * Minus stops at min (1): removals only happen through the trash
   * button, which has its own undo toast.
   * ------------------------------------------------------------ */
  class HvmLineStepper extends HTMLElement {
    connectedCallback() {
      this.input = this.querySelector('input');
      this.min = parseInt(this.dataset.min || '1', 10);
      this.max = this.dataset.max ? parseInt(this.dataset.max, 10) : null;

      this.querySelectorAll('[data-hvm-step]').forEach((button) => {
        button.addEventListener('click', () => {
          this._commit(this._current() + parseInt(button.dataset.hvmStep, 10));
        });
      });

      if (this.input) {
        this.input.addEventListener('change', () => this._commit(this._current()));
        this.input.addEventListener('keydown', (event) => {
          if (event.key === 'Enter') {
            event.preventDefault();
            this.input.blur();
          }
        });
      }
      this._reflect(this._current());
    }

    _current() {
      const value = parseInt((this.input && this.input.value) || this.dataset.quantity || '1', 10);
      return Number.isFinite(value) ? value : this.min;
    }

    _commit(rawValue) {
      let value = Math.max(this.min, Math.round(rawValue) || this.min);
      if (this.max) value = Math.min(value, this.max);
      this._reflect(value);
      this.dispatchEvent(
        new CustomEvent('hvm:line:set', {
          bubbles: true,
          detail: { lineKey: this.dataset.lineKey, quantity: value },
        })
      );
    }

    _reflect(value) {
      if (this.input) this.input.value = value;
      const minus = this.querySelector('[data-hvm-step="-1"]');
      if (minus) minus.toggleAttribute('disabled', value <= this.min);
      const plus = this.querySelector('[data-hvm-step="1"]');
      if (plus && this.max) plus.toggleAttribute('disabled', value >= this.max);
    }
  }
  customElements.define('hvm-line-stepper', HvmLineStepper);

  /* ------------------------------------------------------------ *
   * <hvm-tier-bar> — server-rendered progress; animates from the
   * last known percentage (kept in uiState, survives re-renders).
   * ------------------------------------------------------------ */
  class HvmTierBar extends HTMLElement {
    connectedCallback() {
      const fill = this.querySelector('[data-hvm-fill]');
      if (!fill) return;
      const target = parseFloat(this.dataset.progress || '0');
      const previous = HVMCart.uiState.lastTierPct;

      if (previous === null || previous === target) {
        fill.style.width = `${target}%`;
      } else {
        fill.style.transition = 'none';
        fill.style.width = `${previous}%`;
        requestAnimationFrame(() => {
          requestAnimationFrame(() => {
            fill.style.transition = '';
            fill.style.width = `${target}%`;
          });
        });
      }
      HVMCart.uiState.lastTierPct = target;
    }
  }
  customElements.define('hvm-tier-bar', HvmTierBar);

  /* ------------------------------------------------------------ *
   * <hvm-upsell-carousel> — scroll-snap carousel, no libraries.
   * ------------------------------------------------------------ */
  class HvmUpsellCarousel extends HTMLElement {
    connectedCallback() {
      this.track = this.querySelector('[data-hvm-track]');
      if (!this.track) return;
      this.querySelectorAll('[data-hvm-nav]').forEach((button) => {
        button.addEventListener('click', () => {
          const card = this.track.querySelector('[data-hvm-card]');
          const step = card ? card.getBoundingClientRect().width + 12 : 200;
          this.track.scrollBy({
            left: step * parseInt(button.dataset.hvmNav, 10),
            behavior: 'smooth',
          });
        });
      });
    }
  }
  customElements.define('hvm-upsell-carousel', HvmUpsellCarousel);

  /* ------------------------------------------------------------ *
   * <hvm-coupon-disclosure> — collapsible coupon field. Draft and
   * open state live in uiState so they survive re-renders. Errors
   * never clear the typed code.
   * ------------------------------------------------------------ */
  class HvmCouponDisclosure extends HTMLElement {
    connectedCallback() {
      this.toggle = this.querySelector('[data-hvm-coupon-toggle]');
      this.body = this.querySelector('[data-hvm-coupon-body]');
      this.input = this.querySelector('[data-hvm-coupon-input]');
      this.submit = this.querySelector('[data-hvm-coupon-submit]');
      this.error = this.querySelector('[data-hvm-coupon-error]');
      if (!this.toggle || !this.body) return;

      this._setOpen(HVMCart.uiState.couponOpen, false);
      if (this.input && HVMCart.uiState.couponDraft) this.input.value = HVMCart.uiState.couponDraft;

      this.toggle.addEventListener('click', () => this._setOpen(this.body.hidden, true));

      if (this.input) {
        this.input.addEventListener('input', () => {
          HVMCart.uiState.couponDraft = this.input.value;
          this._clearError();
        });
        this.input.addEventListener('keydown', (event) => {
          if (event.key === 'Enter') {
            event.preventDefault();
            this._apply();
          }
        });
      }
      if (this.submit) this.submit.addEventListener('click', () => this._apply());
    }

    _setOpen(open, focus) {
      this.body.hidden = !open;
      this.toggle.setAttribute('aria-expanded', String(open));
      HVMCart.uiState.couponOpen = open;
      if (open && focus && this.input) {
        this.input.focus();
        this.input.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
      }
    }

    _apply() {
      const code = this.input ? this.input.value.trim() : '';
      if (!code) return;
      if (this.submit) {
        this.submit.setAttribute('disabled', '');
        this.submit.classList.add('is-busy');
      }
      this.dispatchEvent(new CustomEvent('hvm:coupon:apply', { bubbles: true, detail: { code } }));
    }

    showError(message) {
      this.resetBusy();
      if (!this.error) return;
      this.error.hidden = false;
      const text = this.error.querySelector('span') || this.error;
      text.textContent = message;
      if (this.input) this.input.setAttribute('aria-invalid', 'true');
    }

    resetBusy() {
      if (this.submit) {
        this.submit.removeAttribute('disabled');
        this.submit.classList.remove('is-busy');
      }
    }

    _clearError() {
      if (!this.error) return;
      this.error.hidden = true;
      if (this.input) this.input.removeAttribute('aria-invalid');
    }
  }
  customElements.define('hvm-coupon-disclosure', HvmCouponDisclosure);

  /* ------------------------------------------------------------ *
   * Toasts — rendered into the shell's [data-hvm-toasts] container,
   * which lives OUTSIDE #CartDrawer so it survives every re-render.
   * ------------------------------------------------------------ */
  HVMCart.toast = (drawer, message, { actionLabel = null, onAction = null, duration = 4000 } = {}) => {
    const host = drawer.querySelector('[data-hvm-toasts]');
    if (!host) return;

    while (host.children.length >= 2) host.removeChild(host.firstElementChild);

    // No role on the toast itself: the host container already has aria-live,
    // and doubling both makes some screen readers announce twice.
    const toast = document.createElement('div');
    toast.className = 'hvm-toast';

    const text = document.createElement('span');
    text.className = 'hvm-toast__text';
    text.textContent = message;
    toast.appendChild(text);

    let timer;
    let remaining = duration;
    let startedAt = Date.now();
    const dismiss = () => {
      clearTimeout(timer);
      toast.classList.add('is-leaving');
      setTimeout(() => toast.remove(), 200);
    };
    const pause = () => {
      clearTimeout(timer);
      remaining -= Date.now() - startedAt;
    };
    const resume = () => {
      clearTimeout(timer);
      startedAt = Date.now();
      timer = setTimeout(dismiss, Math.max(remaining, 1500));
    };
    // WCAG 2.2.1: give keyboard/AT users a chance — the timer pauses while
    // the toast is hovered or any of its controls has focus.
    toast.addEventListener('mouseenter', pause);
    toast.addEventListener('mouseleave', resume);
    toast.addEventListener('focusin', pause);
    toast.addEventListener('focusout', resume);

    if (actionLabel && onAction) {
      const action = document.createElement('button');
      action.type = 'button';
      action.className = 'hvm-toast__action';
      action.textContent = actionLabel;
      action.addEventListener('click', () => {
        dismiss();
        onAction();
      });
      toast.appendChild(action);
    }

    host.appendChild(toast);
    setTimeout(() => toast.classList.add('is-visible'));
    startedAt = Date.now();
    timer = setTimeout(dismiss, duration);
  };
})();
